/**
 * Presentational component for the trends scatter plot.
 */

import { useState, useEffect } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  Tooltip,
  type TooltipProps,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { formatSeconds } from '@/utils/time';
import { type Average } from '@/types/Average';
import { type ScatterData } from '@/types/ScatterData';
import { TrendLineChart } from './TrendLineChart';
import { DayPartsChart } from './DayPartsChart';
import { RegionsGraph } from './RegionsGraph';
import { useData } from '@/hooks/useData';
import { colors, mutedColors } from '@/utils/Utils';
import { type DayPartData } from '@/types/DayPartData';
import { type RegionsStats } from '@/types/RegionsStats';
import DropdownAnalysisLocationMultiple from './../../dropdowns/DropdownAnalysisLocationMultiple';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import DropdownFilter from '@/components/dropdowns/DropdownFilter';
import { type GroupResponse } from '@/types/GroupResponse';

export default function ScatterPlot({
  trendsData,
  dayPartsData,
  regionsData,
  groupData,
  isLoading,
}: {
  trendsData: Average[];
  dayPartsData: DayPartData[];
  regionsData: RegionsStats[];
  groupData: boolean;
  isLoading: boolean;
}): JSX.Element {
  const [chartData, setChartData] = useState<ScatterData[]>([]);
  const [averageEnabled, setAverageEnabled] = useState<boolean>(false);
  const [trendLineData, setTrendLineData] = useState<Average[] | null>(null);
  const [dayPartData, setDayPartData] = useState<DayPartData[] | null>(null);
  const [regionData, setRegionData] = useState<RegionsStats[] | null>(null);
  const [selectedAnalysisGroups, setSelectedAnalysisGroups] = useState<
    GroupResponse[]
  >([]);
  const [selectedColors, setSelectedColors] = useState<Record<string, string>>(
    {}
  );
  const [avgAdded, setAvgAdded] = useState<boolean>(false);
  const {
    selectedFranchise,
    selectedDates,
    selectedLocations,
    selectedAnalysisLocations,
    setSelectedAnalysisLocations,
    handleSelectedAnalysisLocationsChange,
    handleSyncAnalytics,
    cachedGroups,
  } = useData();
  const [prevLocations, setPrevLocations] = useState<any[]>([]);

  const clearState = () => {
    setTrendLineData(null);
    setDayPartData(null);
    setRegionData(null);
    setSelectedAnalysisLocations([]);
    setSelectedAnalysisGroups([]);
    setSelectedColors({});
    setPrevLocations([]);
  };

  useEffect(() => {
    // clear charts on franchise switch
    clearState();
  }, [selectedFranchise, selectedDates, selectedLocations, groupData]);

  useEffect(() => {
    // using locations
    if (!isLoading) {
      const averageEntry = {
        id: 'average',
        name: 'Average',
        serviceTime: averageX,
        volume: averageY,
        avgRevenue: averageRevenue,
        date: '',
        color: '#888888',
      };
      if (!groupData) {
        const entries: ScatterData[] = [];
        if (selectedAnalysisLocations.length > 0) {
          let entry: ScatterData | undefined;

          if (selectedAnalysisLocations.length < prevLocations.length) {
            // Removed a location
            const removedLocations = prevLocations.filter(
              (loc) => !selectedAnalysisLocations.includes(loc)
            );
            removedLocations.forEach((removedLocation) => {
              entry = chartData.find((entry) => entry.id === removedLocation.id);
              entry && entries.push(entry);
            });
          } else {
            // Added a location
            let newlyAdded = selectedAnalysisLocations.filter(
              (loc) => !prevLocations.includes(loc)
            );
            if (newlyAdded.length === 0) {
              // SJK 12/20/2024: to account for group setting race condition
              newlyAdded = selectedAnalysisLocations;
            }

            newlyAdded.forEach((loc) => {
              entry = chartData.find((entry) => entry.id === loc.id);
              entry && entries.push(entry);
            });
          }
          // check if averages in the selectedTrendsData
          if (
            dayPartData?.find((data) => data.locationId === 'average') != null && regionData?.find((data) => data.locationId === 'average') != null && trendLineData?.find((data) => data.locationId === 'average') != null
          ) {
            entries.push(averageEntry);
          }
        } else if (prevLocations.length > 0) {
          // Removed the final location
          // just use average of all available data in chartData
          let entry: ScatterData | undefined;
          prevLocations.forEach((prevLocations) => {
            entry = chartData.find((entry) => entry.id === prevLocations.id);
            entry && entries.push(entry);
          });
          entries.push(averageEntry);
        } else {
          entries.push(averageEntry);
        }
        entries.length > 0 &&
          entries.forEach((entry) => {
            updateAllState(entry);
          });
        // Set length for next time
        setPrevLocations(selectedAnalysisLocations);
      }
      // using groups
      else {
        const entries: ScatterData[] = [];
        if (selectedAnalysisGroups.length > 0) {
          let entry: ScatterData | undefined;
          if (selectedAnalysisGroups.length < prevLocations.length) {
            // Removed a group
            const removedGroups = prevLocations.filter(
              (grp) =>
                !selectedAnalysisGroups.some((g) => g.groupId === grp.groupId)
            );
            removedGroups.forEach((removedGroup) => {
              entry = chartData.find(
                (entry) => entry.id === removedGroup.groupId
              );
              entry && entries.push(entry);
            });
          } else {
            // Added a group
            let newlyAdded = selectedAnalysisGroups.filter(
              (grp) => !prevLocations.some((g) => g.groupId === grp.groupId)
            );
            if (newlyAdded.length === 0) {
              newlyAdded = selectedAnalysisGroups;
            }
            newlyAdded.forEach((grp) => {
              entry = chartData.find((entry) => entry.id === grp.groupId);
              entry && entries.push(entry);
            });
          }
          if (
            dayPartData?.find((data) => data.locationId === 'average') != null
          ) {
            entries.push(averageEntry);
          }
        } else if (prevLocations.length > 0) {
          let entry: ScatterData | undefined;
          prevLocations.forEach((prevLocation) => {
            entry = chartData.find((entry) => entry.id === prevLocation.groupId);
            entry && entries.push(entry);
          });
          entries.push(averageEntry);
        } else {
          entries.push(averageEntry);
        }
        entries.length > 0 &&
          entries.forEach((entry) => {
            updateAllState(entry);
          });
        setPrevLocations(selectedAnalysisGroups);
      }
    }
  }, [selectedAnalysisLocations, chartData, selectedAnalysisGroups, groupData]);

  useEffect(() => {
    setChartData(getChartData(trendsData));
  }, [trendsData]);

  // Convert average data to scatter plot data
  function getChartData(rawData: Average[]): ScatterData[] {
    const mappedRevenues = mapToRange(
      rawData.map((location) => location.averageRevenue)
    );

    const data = rawData.map((location, index) => {
      const lastTrend = location.trendData.reduce((a, b) =>
        a.date > b.date ? a : b
      );
      const allVolumes =
        location.trendData.reduce((acc, loc) => acc + loc.averageVolume, 0) /
        location.trendData.length;
      const allTimes =
        location.trendData.reduce(
          (acc, loc) => acc + loc.averageServiceTime,
          0
        ) / location.trendData.length;
      return {
        volume: allVolumes,
        serviceTime: allTimes,
        id: location.locationId,
        name: location.locationName,
        date: lastTrend.date, // Use last date, even though this is an average
        color: colors[index % colors.length],
        // location.averageRevenue === 0
        //   ? mutedColors[index % mutedColors.length]
        //   : colors[index % colors.length],
        avgRevenue: location.averageRevenue,
        avgRevenueSize: mappedRevenues[index],
      };
    });
    return data;
  }

  // function to map sizes for the range of
  function mapToRange(
    numbers: number[],
    minTarget: number = 50,
    maxTarget: number = 1250
  ): number[] {
    const filteredNumbers = numbers.filter((num) => num !== 0); // Filter out 0
    const minSource = Math.min(...filteredNumbers);
    const maxSource = Math.max(...filteredNumbers);

    // Create a sorted array of unique values from filteredNumbers
    const uniqueSortedNumbers = Array.from(new Set(filteredNumbers)).sort(
      (a, b) => a - b
    );

    return numbers.map((num) => {
      // Check if num is outside the source range
      if (num < minSource) return minTarget;
      if (num > maxSource) return maxTarget;

      // Find the index of the current number in the unique sorted array
      const index = uniqueSortedNumbers.findIndex((value) => value >= num);

      // Calculate the even mapping
      const mappedValue =
        (index / (uniqueSortedNumbers.length - 1)) * (maxTarget - minTarget) +
        minTarget;

      return mappedValue;
    });
  }

  const updateAllState = (entry: ScatterData) => {
    setSelectedColors((prevMap) => ({
      ...prevMap,
      [entry.id]: entry.color,
    }));

    let selectedTrendsData: Average | undefined;
    let selectedDayPartData: DayPartData | undefined;
    let selectedRegionData: RegionsStats | undefined;

    if (entry.id !== 'average') {
      selectedTrendsData = trendsData.find(
        (data) => data.locationName === entry.name
      );
      selectedDayPartData = dayPartsData.find(
        (data) => data.locationName === entry.name
      );
      selectedRegionData = regionsData.find(
        (data) => data.locationName === entry.name
      );
    } else {
      selectedTrendsData =
        trendsData.length > 0
          ? {
            locationId: 'average',
            locationName: 'Average',
            trendData: trendsData[0].trendData.map((_, idx) => {
              // Find all trendData entries for this date
              const currentDate = trendsData[0].trendData[idx].date;
              const matchingTrends = trendsData
                .map((t) => t.trendData.find((td) => td.date === currentDate))
                .filter((td): td is typeof trendsData[0]['trendData'][0] => !!td);

              const avgVolume =
                matchingTrends.length > 0
                  ? matchingTrends.reduce(
                    (sum, td) => sum + td.averageVolume,
                    0
                  ) / matchingTrends.length
                  : 0;

              const avgServiceTime =
                matchingTrends.length > 0
                  ? matchingTrends.reduce(
                    (sum, td) => sum + td.averageServiceTime,
                    0
                  ) / matchingTrends.length
                  : 0;
              return {
                ...trendsData[0].trendData[idx],
                averageVolume: avgVolume,
                averageServiceTime: avgServiceTime,
                locationId: 'average',
                locationName: 'Average',
              };
            }),
            averageRevenue:
              trendsData.reduce((sum, t) => sum + t.averageRevenue, 0) /
              trendsData.length,
          }
          : undefined;
      selectedDayPartData =
        dayPartsData.length > 0
          ? {
            locationId: 'average',
            locationName: 'Average',

            dayParts: dayPartsData.reduce<
              (typeof dayPartsData)[0]['dayParts']
            >((acc, dayPartsDataEntry) => {
              return acc.concat(
                dayPartsDataEntry.dayParts.map((part) => ({
                  name: part.name,
                  regionKey: part.regionKey,
                  regionName: part.regionName,
                  averageServiceTime:
                    part.averageServiceTime / dayPartsData.length, // doing this to get the average service time across all locations
                  locationId: 'average',
                  locationName: 'Average',
                }))
              );
            }, []),
          }
          : undefined;

      selectedRegionData =
        regionsData.length > 0
          ? {
            locationId: 'average',
            locationName: 'Average',
            regions: (() => {
              // Collect all unique region keys across all locations
              const allRegionKeys = Array.from(
                new Set(
                  regionsData.flatMap((r) =>
                    r.regions.map((region) => region.key)
                  )
                )
              );

              return allRegionKeys.map((regionKey) => {
                // Get all region objects for this regionKey
                const regionEntries = regionsData
                  .map((r) =>
                    r.regions.find((region) => region.key === regionKey)
                  )
                  .filter(Boolean);

                // Calculate average timeSpent for this regionKey
                const avgValue =
                  regionEntries.reduce(
                    (sum, region) => sum + (region?.timeSpent ?? 0),
                    0
                  ) / regionEntries.length;

                // Use the first region object as a template
                const templateRegion = regionEntries[0];

                return {
                  ...templateRegion,
                  value: avgValue,
                  timeSpent: avgValue,
                  locationId: 'average',
                  locationName: 'Average',
                  key: regionKey, // Ensure key is always a string
                };
              });
            })(),
          }
          : undefined;
    }

    if (selectedTrendsData) {
      setTrendLineData((prevData) => {
        if (prevData) {
          if (
            prevData
              .map((tl) => tl.locationId)
              .includes(selectedTrendsData.locationId)
          ) {
            return prevData.filter(
              (tl) => tl.locationId !== selectedTrendsData.locationId
            );
          } else {
            return [...prevData, selectedTrendsData];
          }
        }
        return [selectedTrendsData];
      });
    }

    if (selectedDayPartData) {
      setDayPartData((prevData) => {
        if (prevData) {
          if (
            prevData
              .map((dp) => dp.locationId)
              .includes(selectedDayPartData.locationId)
          ) {
            return prevData.filter(
              (dp) => dp.locationId !== selectedDayPartData.locationId
            );
          } else {
            return [...prevData, selectedDayPartData];
          }
        }
        return [selectedDayPartData];
      });
    }

    if (selectedRegionData) {
      setRegionData((prevData) => {
        if (prevData) {
          if (
            prevData
              .map((r) => r.locationId)
              .includes(selectedRegionData.locationId)
          ) {
            return prevData.filter(
              (r) => r.locationId !== selectedRegionData.locationId
            );
          } else {
            return [...prevData, selectedRegionData];
          }
        }
        return [selectedRegionData];
      });
    }
  };
  const naturalSortOptions = (
    options: Array<{ id: string; value: string }>
  ) => {
    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: 'base',
    });
    return [...options].sort((a, b) => collator.compare(a.value, b.value));
  };

  const handleSelectedAnalysisGroupsChange = (id: string) => {
    const matched = cachedGroups.find((group) => group.groupId === id);
    if (matched != null) {
      setSelectedAnalysisGroups(
        selectedAnalysisGroups.some((sm) => sm.groupId === id)
          ? selectedAnalysisGroups.filter((v) => v.groupId !== matched.groupId)
          : [...selectedAnalysisGroups, matched]
      );
    }
  };

  // Add newly clicked data to the selectedAnalysisLocations
  const handlePointClick = (entry: ScatterData) => {
    handleSelectedAnalysisLocationsChange(entry.id);
  };

  const CustomTooltip: React.FC<TooltipProps<any, any>> = ({
    active,
    payload,
  }) => {
    if (active && payload?.length) {
      const { name, volume, serviceTime, avgRevenue, color } =
        payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: '#fff',
            border: '1px solid #ccc',
            padding: '5px',
            borderRadius: '4px',
            fontSize: 'small',
          }}
        >
          <p>
            <span
              style={{
                width: '1em',
                height: '1em',
                backgroundColor: color,
                marginRight: '0.5em',
                display: 'inline-block',
              }}
            />
            {`${name}`}
          </p>
          <p>{`Volume: ${volume.toFixed(2)}`}</p>
          <p>{`Service Time: ${formatSeconds(serviceTime)}`}</p>
          <p>
            {avgRevenue === 0
              ? 'NO REVENUE DATA'
              : `Daily Revenue: $${avgRevenue.toFixed(2)}`}
          </p>
        </div>
      );
    }

    return null;
  };

  const averageX =
    chartData.reduce((sum, d) => sum + d.serviceTime, 0) / chartData.length;
  const averageY =
    chartData.reduce((sum, d) => sum + d.volume, 0) / chartData.length;
  const averageRevenue =
    chartData.reduce((sum, d) => sum + d.avgRevenue, 0) /
    chartData.filter((d) => d.avgRevenue !== 0).length;
  const maxDiffX = chartData.reduce((maxDiff, d) => {
    const diff = Math.abs(d.serviceTime - averageX);
    return Math.max(maxDiff, diff);
  }, 0);
  const maxDiffY = chartData.reduce((maxDiff, d) => {
    const diff = Math.abs(d.volume - averageY);
    return Math.max(maxDiff, diff);
  }, 0);

  const height = 595;

  return (
    <div>
      <div className="flex flex-col col-span-full sm:col-span-6 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700">
        <header className="px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between">
          <div>
            <h2 className="font-semibold text-slate-800 dark:text-slate-100">
              Store Performance
            </h2>
            <h5 className="text-slate-600 dark:text-slate-100">
              Service Time and Customer Count
            </h5>
          </div>

          <button
            className="btn border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-gray-300 text-slate-500 dark:text-slate-400 w-6/12 hover:bg-slate-300 dark:hover:bg-slate-600 transition sm:w-auto"
            onClick={() => {
              setAverageEnabled((prev) => !prev);
            }}
          >
            {averageEnabled ? 'Hide Average' : 'Show Average'}
          </button>
        </header>
        {(isLoading || chartData === undefined || chartData.length === 0) && (
          <>
            <ResponsiveContainer className="grow" height={height}>
              <>
                <ScatterChart
                  margin={{
                    top: 20,
                    right: 20,
                    bottom: 20,
                    left: 20,
                  }}
                >
                  <XAxis
                    type="number"
                    dataKey="serviceTime"
                    name="serviceTime"
                    tickCount={7}
                    tick={{ fontSize: 12, fill: '#808080' }}
                    tickFormatter={(value) => formatSeconds(value)}
                    domain={[
                      averageX - maxDiffX - 10,
                      averageX + maxDiffX + 10,
                    ]}
                  />
                  <YAxis
                    type="number"
                    dataKey="volume"
                    name="volume"
                    tickCount={7}
                    tick={{ fontSize: 12, fill: '#808080' }}
                    tickFormatter={(value) => `${value.toFixed(0)} count`}
                    domain={[
                      averageY - maxDiffY - 50,
                      averageY + maxDiffY + 50,
                    ]}
                  />
                  <ZAxis
                    type="number"
                    dataKey="avgRevenueSize"
                    range={[10, 1250]}
                    name="Daily Revenue Avg"
                  />
                  <Tooltip content={<CustomTooltip />} cursor={false} />
                </ScatterChart>
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10,
                  }}
                >
                  <LoadingSpinner />
                </div>
              </>
            </ResponsiveContainer>
          </>
        )}
        {!isLoading && chartData.length !== 0 && (
          <ResponsiveContainer className="grow" height={height}>
            <ScatterChart
              margin={{
                top: 20,
                right: 20,
                bottom: 20,
                left: 20,
              }}
            >
              <XAxis
                type="number"
                dataKey="serviceTime"
                name="serviceTime"
                tickCount={7}
                tick={{ fontSize: 12, fill: '#808080' }}
                tickFormatter={(value) => formatSeconds(value)}
                domain={[averageX - maxDiffX - 10, averageX + maxDiffX + 10]}
              />
              <YAxis
                type="number"
                dataKey="volume"
                name="volume"
                tickCount={7}
                tick={{ fontSize: 12, fill: '#808080' }}
                tickFormatter={(value) => `${value.toFixed(0)} count`}
                domain={[averageY - maxDiffY - 50, averageY + maxDiffY + 50]}
              />
              <ZAxis
                type="number"
                dataKey="avgRevenueSize"
                range={[10, 1250]}
                name="Daily Revenue Avg"
              />
              <Tooltip content={<CustomTooltip />} cursor={false} />
              {chartData?.map((entry, index) => (
                <Scatter
                  key={entry.name}
                  name={entry.name}
                  data={[entry]}
                  fill={
                    colors[index % colors.length]
                    // entry.avgRevenue === 0
                    //   ? mutedColors[index % mutedColors.length]
                    //   : colors[index % colors.length]
                  }
                  onClick={() => {
                    handlePointClick(entry);
                  }}
                />
              ))}
              {/* Average point */}
              {averageEnabled && (
                <Scatter
                  key="average-point"
                  name="Average"
                  data={[
                    {
                      serviceTime: averageX,
                      volume: averageY,
                      avgRevenue: averageRevenue,
                      avgRevenueSize: 1250,
                      id: 'average',
                      name: 'Average',
                      color: '#D3D3D3', // black or any distinct color
                      opacity: 0.2,
                    },
                  ]}
                  fill="#D3D3D3"
                />
              )}
              <ReferenceLine x={averageX} stroke="#D3D3D3" />
              <ReferenceLine y={averageY} stroke="#D3D3D3" />
            </ScatterChart>
          </ResponsiveContainer>
        )}
        <footer className="px-4 py-4 dark:border-slate-700">
          <h5 className="text-sm text-slate-600 dark:text-slate-100">
            The further the points get to the right, the slower the service
            time. <br />
            The further the points get to the top, the higher the customer
            count.
          </h5>
        </footer>
      </div>
      <div className="sm:flex sm:justify-left sm:items-center mb-4">
        {/* Left: Title */}
        <div className="mt-4 mb-4 sm:mb-0">
          <h3 className="text-xl text-slate-700 dark:text-slate-100">
            Detailed View 🔎
          </h3>
        </div>
        <div className="sm:flex sm:justify-between sm:items-center m-4 gap-4">
          {groupData ? (
            // Replace this with your custom group component
            <DropdownFilter
              title={`${selectedAnalysisGroups.length} Groups Selected`}
              options={naturalSortOptions(
                cachedGroups.map((group) => ({
                  id: group.groupId,
                  value: group.displayName,
                }))
              )}
              selected={selectedAnalysisGroups.map((group) => ({
                id: group.groupId,
                value: group.displayName,
              }))}
              handleChange={handleSelectedAnalysisGroupsChange}
            />
          ) : (
            <DropdownAnalysisLocationMultiple />
          )}
        </div>
        <div className="sm:flex sm:justify-between sm:items-center">
          <button
            data-tooltip-target="tooltip-dark"
            className="btn border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-gray-300 text-slate-500 dark:text-slate-400 w-6/12 sm:w-auto"
            onClick={handleSyncAnalytics}
          >
            Sync
          </button>
        </div>
      </div>
      <div className="flex flex-wrap bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700">
        <TrendLineChart
          averageData={trendLineData}
          chartType="volume"
          colorMap={selectedColors}
          averageEnabled={averageEnabled}
        />
        <TrendLineChart
          averageData={trendLineData}
          chartType="serviceTime"
          colorMap={selectedColors}
          averageEnabled={averageEnabled}
        />
        <DayPartsChart
          dayPartsData={dayPartData}
          colorMap={selectedColors}
          averageEnabled={averageEnabled}
        />
        <RegionsGraph
          regionsData={regionData}
          colorMap={selectedColors}
          averageEnabled={averageEnabled}
        />
      </div>
    </div>
  );
}
